import { BREAKPOINT } from "@/configs/breakpoints";
import GlobalOptions from "@/utils/global-options";
import { INFO_CARD_TYPE } from "./card-types";

/**
 * Per-card type full-width responsive breakpoints.
 */
export const cardTypeFullWidthBreakpoints: Partial<Record<INFO_CARD_TYPE, BREAKPOINT>> = {
    [INFO_CARD_TYPE.WEATHER]: BREAKPOINT.WEATHER_CARD_FULL_WIDTH,
    [INFO_CARD_TYPE.DEVICE]: BREAKPOINT.DEVICE_CARD_FULL_WIDTH,
    [INFO_CARD_TYPE.ZONE]: BREAKPOINT.ZONE_CARD_FULL_WIDTH,
    [INFO_CARD_TYPE.AOI]: BREAKPOINT.AOI_CARD_FULL_WIDTH,
    // SEARCH (left slot) and ADD_DEVICE have no dedicated breakpoint; reuse DEVICE_CARD_FULL_WIDTH
    // so mobile panning uses top-anchored offsets rather than side-anchored.
    [INFO_CARD_TYPE.SEARCH]: BREAKPOINT.DEVICE_CARD_FULL_WIDTH,
    [INFO_CARD_TYPE.ADD_DEVICE]: BREAKPOINT.DEVICE_CARD_FULL_WIDTH,
};

/**
 * Cards that are always treated as full-width at every viewport size.
 */
export const alwaysFullWidthCardTypes = new Set<INFO_CARD_TYPE>([
    INFO_CARD_TYPE.SCHEDULER,
    INFO_CARD_TYPE.IRRIGATION_REPORT,
]);

/**
 * Cards currently supported by the card container.
 */
export const currentlySupportedInfoCardTypes = [
    INFO_CARD_TYPE.DEVICE,
    INFO_CARD_TYPE.WEATHER,
    INFO_CARD_TYPE.ZONE,
    INFO_CARD_TYPE.AOI,
];

/**
 * Types of cards whose related map entities are currently integrated into Zustand.
 */
export const zustandMapEntityCardTypes = [INFO_CARD_TYPE.DEVICE, INFO_CARD_TYPE.ZONE, INFO_CARD_TYPE.AOI];

/**
 * These cards are not usurped (automatically closed) by another action. They must be explicitly
 * closed by the user.
 */
export const persistentCardTypes = [INFO_CARD_TYPE.SCHEDULER, INFO_CARD_TYPE.IRRIGATION_REPORT];

const cardTypesThatHideChips: ReadonlyArray<INFO_CARD_TYPE> = [
    INFO_CARD_TYPE.ADD_DEVICE,
    INFO_CARD_TYPE.SCHEDULER,
    INFO_CARD_TYPE.IRRIGATION_REPORT,
    INFO_CARD_TYPE.WEATHER,
] as const;

/**
 * Determines if chips should be hidden when a card is open.
 */
export const doesCardTypeHideChips = (cardType: INFO_CARD_TYPE | undefined): boolean => {
    if (!GlobalOptions.showChipsAtSameTimeAsInfoCards) {
        return true;
    }
    if (!cardType) {
        return false;
    }
    return cardTypesThatHideChips.includes(cardType);
};

export enum CARD_SLOT {
    BOTTOM_LEFT = "bottom-left",
    BOTTOM_RIGHT = "bottom-right",
}

/**
 * Mapping of card type to where the card gets rendered.
 */
export const CARD_TYPE_TO_CARD_SLOT: Record<INFO_CARD_TYPE, CARD_SLOT> = {
    [INFO_CARD_TYPE.SEARCH]: CARD_SLOT.BOTTOM_LEFT,
    [INFO_CARD_TYPE.DEVICE]: CARD_SLOT.BOTTOM_RIGHT,
    [INFO_CARD_TYPE.WEATHER]: CARD_SLOT.BOTTOM_RIGHT,
    [INFO_CARD_TYPE.ZONE]: CARD_SLOT.BOTTOM_RIGHT,
    [INFO_CARD_TYPE.AOI]: CARD_SLOT.BOTTOM_RIGHT,
    [INFO_CARD_TYPE.ADD_DEVICE]: CARD_SLOT.BOTTOM_RIGHT,
    [INFO_CARD_TYPE.SCHEDULER]: CARD_SLOT.BOTTOM_RIGHT,
    [INFO_CARD_TYPE.IRRIGATION_REPORT]: CARD_SLOT.BOTTOM_RIGHT,
};
